import { useEffect, useState } from "react";
import { KeyRound, Lock, Unlock, Copy, UserPlus, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, C, errorMessage, fmtChips } from "../../lib/api";
import { Card } from "./ui";

// Invite-only room: public sign-up switch, issue a sign-in to a tester, and the kept list to copy and paste from later.
export const InvitePanel = ({ onCreated }) => {
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ email: "", display_name: "", password: "", chips: "", email_invite: false });
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);
  const [kept, setKept] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const loadKept = () => api.get(C.ApiRoutes.ADMIN_SIGNINS).then((r) => setKept(r.data.signins)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { api.get(C.ApiRoutes.ADMIN_SIGNUPS).then((r) => setOpen(r.data.open)).catch((e) => toast.error(errorMessage(e))); loadKept(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async () => {
    try {
      const { data } = await api.post(C.ApiRoutes.ADMIN_SIGNUPS, { open: !open });
      setOpen(data.open);
      toast.success(data.open ? "Public sign-up is open" : "Invite-only: Create account is hidden and /register refuses");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(C.ApiRoutes.ADMIN_PLAYERS, {
        email: form.email.trim(),
        display_name: form.display_name.trim(),
        password: form.password.trim() || undefined,
        chips: form.chips === "" ? undefined : Number(form.chips),
        email_invite: form.email_invite,
      });
      setIssued({ email: data.player.email, password: data.password, name: data.player.display_name, balance: data.balance, mail: data.email });
      setForm({ email: "", display_name: "", password: "", chips: "", email_invite: form.email_invite });
      loadKept();
      onCreated?.();
    } catch (ex) { toast.error(errorMessage(ex)); } finally { setBusy(false); }
  };

  const copyPair = (email, password) => {
    navigator.clipboard?.writeText(`Players Room sign-in\nemail: ${email}\npassword: ${password}`);
    toast.success("Sign-in copied — paste it to your tester");
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(C.ApiRoutes.ADMIN_EMAIL_TEST, { to: testTo.trim() });
      if (data.status === "sent") toast.success(`Test email sent to ${testTo} via ${data.host}`);
      else if (data.status === "disabled") toast.error("Email isn't configured on the server yet (SMTP_* settings)");
      else toast.error(`Send failed: ${data.error}`);
    } catch (e) { toast.error(errorMessage(e)); } finally { setTesting(false); }
  };

  const mailNote = (mail) => {
    if (!mail || mail.status === "skipped") return "Not emailed — pass it on yourself.";
    if (mail.status === "sent") return "Emailed to the player as well.";
    if (mail.status === "disabled") return "Email isn't set up on the server, so send it yourself.";
    return `Email failed (${mail.error}) — send it yourself.`;
  };

  return (
    <Card title="Sign-ins">
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm text-amber-50 flex items-center gap-2">
              {open === false ? <Lock className="w-4 h-4 text-amber-300" /> : <Unlock className="w-4 h-4 text-emerald-300" />}
              {open === null ? "…" : open ? "Anyone can create an account" : "Invite-only — staff issue sign-ins"}
            </div>
            <p className="text-xs text-slate-400 mt-1">While invite-only, the landing page shows Sign in only and self-registration is refused.</p>
          </div>
          <button data-testid="signups-toggle" data-open={open ? "true" : "false"} disabled={open === null} onClick={toggle}
            className={`rounded-full px-4 py-2 text-xs font-display tracking-wider btn-lift disabled:opacity-40 ${open ? "bg-amber-500 text-slate-900" : "bg-white/10 text-amber-200"}`}>
            {open ? "Close sign-up" : "Open sign-up"}
          </button>
        </div>

        <form onSubmit={create} data-testid="create-player-form" className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end border-t border-white/5 pt-4">
          <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wider text-slate-400 md:col-span-2">Email
            <input data-testid="create-player-email" type="email" required value={form.email} onChange={set("email")} placeholder="tester@example.com"
              className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-amber-400 outline-none" />
          </label>
          <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wider text-slate-400">Screen name
            <input data-testid="create-player-name" required minLength={2} maxLength={24} value={form.display_name} onChange={set("display_name")} placeholder="Dave"
              className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-amber-400 outline-none" />
          </label>
          <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wider text-slate-400">Password <span className="text-slate-500 normal-case tracking-normal">(blank = generate)</span>
            <input data-testid="create-player-password" minLength={8} maxLength={128} value={form.password} onChange={set("password")} placeholder="auto"
              className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono text-slate-100 focus:border-amber-400 outline-none" />
          </label>
          <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wider text-slate-400">Chips
            <input data-testid="create-player-chips" type="number" min={0} value={form.chips} onChange={set("chips")} placeholder={String(C.TDA.STARTING_CHIPS)}
              className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono text-slate-100 focus:border-amber-400 outline-none" />
          </label>
          <div className="md:col-span-5 flex flex-wrap items-center gap-4">
            <button data-testid="create-player-submit" disabled={busy} className="rounded-full px-5 py-2 text-xs font-display tracking-wider bg-amber-500 hover:bg-amber-400 text-slate-900 btn-lift disabled:opacity-40 flex items-center gap-2">
              <UserPlus className="w-3.5 h-3.5" /> {busy ? "Creating…" : "Create sign-in"}
            </button>
            <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
              <input data-testid="create-player-email-invite" type="checkbox" checked={form.email_invite}
                onChange={(e) => setForm({ ...form, email_invite: e.target.checked })} className="accent-amber-500" />
              Email the sign-in to the player
            </label>
          </div>
        </form>

        {issued && (
          <div data-testid="created-player-credentials" className="rounded-xl border border-amber-400/40 bg-amber-400/5 p-4 flex flex-col gap-2">
            <div className="text-xs uppercase tracking-[0.2em] text-amber-300 flex items-center gap-2"><KeyRound className="w-3.5 h-3.5" /> {issued.name} · {fmtChips(issued.balance)}</div>
            <div className="font-mono text-sm text-amber-50 break-all">{issued.email}</div>
            <div className="font-mono text-base text-amber-200 break-all" data-testid="created-player-password">{issued.password}</div>
            <p className="text-xs text-slate-400">Shown once and never stored — copy it now. {mailNote(issued.mail)} Afterwards the only way in is a one-time reset link from the Players list.</p>
            <div className="flex gap-2">
              <button data-testid="copy-credentials" onClick={() => copyPair(issued.email, issued.password)} className="rounded-full px-3 py-1.5 text-xs bg-white/10 hover:bg-white/20 btn-lift flex items-center gap-1.5"><Copy className="w-3 h-3" /> Copy</button>
              <button data-testid="dismiss-credentials" onClick={() => setIssued(null)} className="rounded-full px-3 py-1.5 text-xs text-slate-400 hover:text-white">Done</button>
            </div>
          </div>
        )}

        {kept.length > 0 && (
          <div className="border-t border-white/5 pt-4" data-testid="kept-signins">
            <p className="text-[11px] text-slate-400">
              {kept.length} older {kept.length === 1 ? "account still has a password" : "accounts still have passwords"} saved from the old invite system.
              They are no longer shown anywhere — wipe them from the Players list whenever you like.
            </p>
          </div>
        )}

        <div className="border-t border-white/5 pt-4 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wider text-slate-400 flex-1 min-w-[220px]">Check the mail settings
            <input data-testid="test-email-to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="your@email.com"
              className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-amber-400 outline-none" />
          </label>
          <button data-testid="test-email-send" disabled={testing || !testTo.trim()} onClick={sendTest}
            className="rounded-full px-4 py-2 text-xs font-display tracking-wider bg-white/10 hover:bg-white/20 text-amber-200 btn-lift disabled:opacity-40 flex items-center gap-2">
            <Mail className="w-3.5 h-3.5" /> {testing ? "Sending…" : "Send test"}
          </button>
        </div>
      </div>
    </Card>
  );
};
